/**
 * Comprehensive prescription translation utility
 * Handles all prescription-related translations with proper fallbacks
 * Follows the existing codebase patterns and provides consistent translation support
 */

import translations from '../views/admin/doctors/translations.json';

/**
 * Get comprehensive prescription translations for a specific language
 * @param {string} language - Target language (default: 'english')
 * @returns {object} Complete set of translated prescription labels
 */
export const getPrescriptionTranslations = (language = 'english') => {
  // Get translation data for the specified language, fallback to English
  const translation = translations[language] || translations.english;
  const englishFallback = translations.english;

  return {
    // Section headings - with proper fallbacks
    patient_details: translation.patient_details || englishFallback.patient_details || 'Patient Details',
    date: translation.date || englishFallback.date || 'Date',
    parameters: translation.parameters || englishFallback.parameters || 'parameters',
    // chief_complaints: translation.chief_complaints || englishFallback.chief_complaints || 'Chief Complaints',
    // diagnosis: translation.diagnosis || englishFallback.diagnosis || 'Diagnosis',
    medicines: translation.medicines || englishFallback.medicines || 'Medicines',
    investigations: translation.Investigations || englishFallback.Investigations || 'Investigations',
    advice: translation.advice || englishFallback.advice || 'Advice',
    follow_up: translation.follow_up || englishFallback.follow_up || 'Follow up',
    referred_to: translation.referred_to || englishFallback.referred_to || 'Referred To',

    // parameters labels - with proper fallbacks
    height: translation.height || englishFallback.height || 'Height',
    weight: translation.weight || englishFallback.weight || 'Weight',
    bp: translation.bp || englishFallback.bp || 'BP',
    spo2: translation.spo2 || englishFallback.spo2 || 'SpO2',
    pulse: translation.pulse || englishFallback.pulse || 'Pulse',
    temp: translation.temp || englishFallback.temp || 'Temp',

    // Medicine table headers - with proper fallbacks
    medicine: translation.medicine || englishFallback.medicine || 'Medicine',
    dosage: translation.dosage || englishFallback.dosage || 'Dosage',
    duration: translation.duration || englishFallback.duration || 'Duration',
    frequency: translation.frequency || englishFallback.frequency || 'Frequency',
    duration_instructions: translation.duration_instructions || englishFallback.duration_instructions || 'Duration & Instructions',

    // Units - with proper fallbacks
    cms: translation.cms || englishFallback.cms || 'cms',
    kg: translation.kg || englishFallback.kg || 'kg',
    mmhg: translation.mmhg || englishFallback.mmhg || 'mmhg',
    percent: translation.percent || englishFallback.percent || '%',
    bpm: translation.bpm || englishFallback.bpm || 'bpm',
    celsius: translation.celsius || englishFallback.celsius || 'C',
    
    // Medicine quantity/duration units - with proper fallbacks
    days: translation.days || englishFallback.days || 'days',
    units: translation.units || englishFallback.units || 'units',

    // Time periods - with proper fallbacks
    morning: translation.morning || englishFallback.morning || 'Morning',
    afternoon: translation.afternoon || englishFallback.afternoon || 'Afternoon',
    evening: translation.evening || englishFallback.evening || 'Evening',
    night: translation.night || englishFallback.night || 'Night',

    // Duration periods - with proper fallbacks
    '1_day': translation['1_day'] || englishFallback['1_day'] || '1 day',
    '2_days': translation['2_days'] || englishFallback['2_days'] || '2 days',
    '3_days': translation['3_days'] || englishFallback['3_days'] || '3 days',
    '1_week': translation['1_week'] || englishFallback['1_week'] || '1 week',
    '2_weeks': translation['2_weeks'] || englishFallback['2_weeks'] || '2 weeks',

    // Instructions - with proper fallbacks
    before_food: translation.before_food || englishFallback.before_food || 'Before food',
    after_food: translation.after_food || englishFallback.after_food || 'After food',

    // Medicine types - with proper fallbacks
    tablet: translation.tablet || englishFallback.tablet || 'Tablet',
    tablets: translation.tablets || englishFallback.tablets || 'Tablets',

    // Patient info - with proper fallbacks
    patientName: translation.patientName || englishFallback.patientName || 'Patient Name',
    age: translation.age || englishFallback.age || 'Age',
    doctor: translation.doctor || englishFallback.doctor || 'Doctor'
  };
};

/**
 * Translate a specific text key for prescription
 * @param {string} key - Translation key
 * @param {string} language - Target language
 * @returns {string} Translated text with fallback
 */
export const translatePrescriptionText = (key, language = 'english') => {
  const prescriptionTranslations = getPrescriptionTranslations(language);
  return prescriptionTranslations[key] || key;
};

/**
 * Enhanced frequency translation with comprehensive mapping
 * @param {string} frequencyText - Frequency text to translate
 * @param {string} language - Target language
 * @returns {string} Translated frequency text
 */
export const translateFrequencyText = (frequencyText, language = 'english') => {
  if (!frequencyText || language === 'english') {
    return frequencyText;
  }

  const translations = getPrescriptionTranslations(language);
  let translatedText = frequencyText;

  // Enhanced frequency mapping with more comprehensive patterns
  const frequencyMap = {
    'Morning': translations.morning,
    'Afternoon': translations.afternoon,
    'Evening': translations.evening,
    'Night': translations.night,
    'Before food': translations.before_food,
    'After food': translations.after_food,
    'before meals': translations.before_food,
    'after meals': translations.after_food,
    'Before meals': translations.before_food,
    'After meals': translations.after_food
  };

  // Apply translations with case-insensitive matching
  Object.entries(frequencyMap).forEach(([english, translated]) => {
    if (translated) {
      const regex = new RegExp(english, 'gi');
      translatedText = translatedText.replace(regex, translated);
    }
  });

  return translatedText;
};

/**
 * Enhanced duration translation with comprehensive mapping
 * @param {string} durationText - Duration text to translate
 * @param {string} language - Target language
 * @returns {string} Translated duration text
 */
export const translateDurationText = (durationText, language = 'english') => {
  if (!durationText || language === 'english') {
    return durationText;
  }

  const translations = getPrescriptionTranslations(language);
  let translatedText = durationText;

  // Enhanced duration mapping with more comprehensive patterns
  const durationMap = {
    '1 day': translations['1_day'],
    '2 days': translations['2_days'],
    '3 days': translations['3_days'],
    '4 days': translations['4_days'] || `4 ${translations['2_days']?.split(' ')[1] || 'days'}`,
    '5 days': translations['5_days'] || `5 ${translations['2_days']?.split(' ')[1] || 'days'}`,
    '1 week': translations['1_week'],
    '2 weeks': translations['2_weeks'],
    '3 weeks': translations['3_weeks'] || `3 ${translations['2_weeks']?.split(' ')[1] || 'weeks'}`,
    '1 month': translations['1_month'] || `1 ${translations['1_week']?.split(' ')[1] || 'month'}`,
    '2 months': translations['2_months'] || `2 ${translations['1_week']?.split(' ')[1] || 'months'}`
  };

  // Apply translations with exact matching
  Object.entries(durationMap).forEach(([english, translated]) => {
    if (translated) {
      const regex = new RegExp(`\\b${english}\\b`, 'gi');
      translatedText = translatedText.replace(regex, translated);
    }
  });

  return translatedText;
};

/**
 * Enhanced instruction translation with comprehensive mapping
 * @param {string} instructionText - Instruction text to translate
 * @param {string} language - Target language
 * @returns {string} Translated instruction text
 */
export const translateInstructionText = (instructionText, language = 'english') => {
  if (!instructionText || language === 'english') {
    return instructionText;
  }

  const translations = getPrescriptionTranslations(language);
  let translatedText = instructionText;

  // Enhanced instruction mapping with more comprehensive patterns
  const instructionMap = {
    'before food': translations.before_food,
    'after food': translations.after_food,
    'Before food': translations.before_food,
    'After food': translations.after_food,
    'before meals': translations.before_food,
    'after meals': translations.after_food,
    'Before meals': translations.before_food,
    'After meals': translations.after_food,
    'with food': translations.with_food || translations.after_food,
    'empty stomach': translations.empty_stomach || translations.before_food,
    'on empty stomach': translations.empty_stomach || translations.before_food
  };

  // Apply translations with case-insensitive matching
  Object.entries(instructionMap).forEach(([english, translated]) => {
    if (translated) {
      const regex = new RegExp(english, 'gi');
      translatedText = translatedText.replace(regex, translated);
    }
  });

  return translatedText;
};

/**
 * Check if text contains Unicode characters (non-ASCII)
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains Unicode characters
 */
export const hasUnicodeCharacters = (text) => {
  return /[^\x00-\x7F]/.test(text);
};

/**
 * Get language-specific font configuration
 * @param {string} language - Target language
 * @returns {object} Font configuration for the language
 */
export const getLanguageFontConfig = (language = 'english') => {
  const fontConfigs = {
    english: { font: 'helvetica', needsUnicodeSupport: false },
    hindi: { font: 'helvetica', needsUnicodeSupport: true },
    bengali: { font: 'helvetica', needsUnicodeSupport: true },
    gujarati: { font: 'helvetica', needsUnicodeSupport: true },
    marathi: { font: 'helvetica', needsUnicodeSupport: true },
    tamil: { font: 'helvetica', needsUnicodeSupport: true },
    punjabi: { font: 'helvetica', needsUnicodeSupport: true },
    telugu: { font: 'helvetica', needsUnicodeSupport: true },
    kannada: { font: 'helvetica', needsUnicodeSupport: true },
    malayalam: { font: 'helvetica', needsUnicodeSupport: true },
    assamese: { font: 'helvetica', needsUnicodeSupport: true }
  };

  return fontConfigs[language] || fontConfigs.english;
};

/**
 * Create a comprehensive translation context for prescription rendering
 * @param {string} language - Target language
 * @returns {object} Complete translation context with all necessary functions and data
 */
export const createPrescriptionTranslationContext = (language = 'english') => {
  const translations = getPrescriptionTranslations(language);
  const fontConfig = getLanguageFontConfig(language);

  return {
    // Translation data
    labels: translations,
    language,
    
    // Translation functions
    translateText: (key) => translatePrescriptionText(key, language),
    translateFrequency: (text) => translateFrequencyText(text, language),
    translateDuration: (text) => translateDurationText(text, language),
    translateInstructions: (text) => translateInstructionText(text, language),
    
    // Utility functions
    hasUnicode: hasUnicodeCharacters,
    fontConfig,
    
    // Helper function to get translated text with fallback
    getTranslatedLabel: (key, fallback = key) => {
      return translations[key] || fallback;
    }
  };
};

// Export all functions as default object for easy importing
export default {
  getPrescriptionTranslations,
  translatePrescriptionText,
  translateFrequencyText,
  translateDurationText,
  translateInstructionText,
  hasUnicodeCharacters,
  getLanguageFontConfig,
  createPrescriptionTranslationContext
};